import { Criticidade, Prioridade, StatusAtividade, TipoExcecao, TipoReceita } from '../models/atividade';
import { StatusCiclo, TipoEtapaSetup, TipoFeriado } from '../models/ciclo';
import { ClassificacaoDesempenho } from '../models/inteligencia';
import { TipoProduto } from '../models/produtos';
import { NivelAcessoMaestro, PerfilUsuario, TipoArea } from '../models/scope';
import { SeveridadeRisco, StatusRisco } from '../models/risco';
import { StatusDelegacao } from '../models/delegacao';

// O backend (MaestroModuleSetupEtapaResponseDTO) só devolve o `tipo` (string) de cada etapa —
// sem título/descrição/ícone. Esses três mapas existem só no frontend, o dado de progresso em si
// (concluída/evidência/quem concluiu) é 100% real, vindo da API.
export const LABEL_ETAPA_SETUP: Record<TipoEtapaSetup, string> = {
  MODELO: 'Modelo de fechamento',
  PERFIS_NIVEIS: 'Perfis e níveis de acesso',
  RESPONSAVEIS: 'Responsáveis',
  ESCOPOS: 'Grupos e escopos',
  AREAS_PROCESSOS: 'Áreas e processos',
  TEMPLATES_SLA: 'Templates e SLAs',
  EVIDENCIAS_REGRAS: 'Evidências e regras',
  REVISAO: 'Revisão final',
};

export const DESCRICAO_ETAPA_SETUP: Record<TipoEtapaSetup, string> = {
  MODELO: 'Definição do modelo de fechamento contábil do grupo — fases, hierarquia de atividades e templates de referência.',
  PERFIS_NIVEIS: 'Cadastro dos perfis de usuário e níveis de acesso que vão operar o Maestro Contábil.',
  RESPONSAVEIS: 'Definição dos responsáveis por área e empresa — preparadores, revisores e aprovadores.',
  ESCOPOS: 'Cadastro do Grupo Econômico, dos Grupos de Empresas e do vínculo com as empresas do cliente.',
  AREAS_PROCESSOS: 'Cadastro das áreas envolvidas no fechamento e dos processos/macrotarefas de cada uma.',
  TEMPLATES_SLA: 'Definição dos templates de fechamento e dos prazos (SLA) de cada etapa.',
  EVIDENCIAS_REGRAS: 'Regras de evidência obrigatória, validação e aprovação SOX das atividades.',
  REVISAO: 'Revisão final de toda a configuração antes de habilitar o módulo.',
};

export const ICONE_ETAPA_SETUP: Record<TipoEtapaSetup, string> = {
  MODELO: 'bi bi-diagram-3',
  PERFIS_NIVEIS: 'bi bi-person-badge',
  RESPONSAVEIS: 'bi bi-people',
  ESCOPOS: 'bi bi-collection',
  AREAS_PROCESSOS: 'bi bi-bounding-box',
  TEMPLATES_SLA: 'bi bi-hourglass-split',
  EVIDENCIAS_REGRAS: 'bi bi-shield-check',
  REVISAO: 'bi bi-check2-square',
};

// Evidência sugerida (pré-preenchida, editável) ao concluir cada etapa — mesmo padrão do
// Workflow SOX (JUSTIFICATIVAS_SOX_PADRAO em OnboardingWizardPage.tsx), adaptado às 8 etapas reais.
export const EVIDENCIA_PADRAO_ETAPA_SETUP: Record<TipoEtapaSetup, string> = {
  MODELO: 'Modelo de fechamento contábil definido e validado com a área de Controladoria/TI.',
  PERFIS_NIVEIS: 'Perfis de usuário e níveis de acesso cadastrados e validados conforme a política de segurança do grupo.',
  RESPONSAVEIS: 'Responsáveis (preparadores, revisores e aprovadores) definidos por área e empresa, conforme a estrutura organizacional.',
  ESCOPOS: 'Grupo Econômico, Grupos de Empresas e vínculo com as empresas cadastrados e conferidos.',
  AREAS_PROCESSOS: 'Áreas e processos do fechamento cadastrados e validados conforme o organograma vigente.',
  TEMPLATES_SLA: 'Templates de fechamento e prazos (SLA) definidos e validados com as áreas responsáveis.',
  EVIDENCIAS_REGRAS: 'Regras de evidência obrigatória e aprovação SOX configuradas e validadas.',
  REVISAO: 'Configuração revisada integralmente — todas as etapas anteriores conferidas antes da habilitação do módulo.',
};

// AGUARDANDO_DEPENDENCIA é, no backend, o próprio equivalente do "Bloqueada" do vocabulário do
// épico (bloqueio por dependência não satisfeita — RN-27) — decisão deliberada de não duplicar em
// outro status técnico (handoff_maestro_contabil.md secção 5). O rótulo NÃO usa a palavra
// "Bloqueada": DependenciaBadge.tsx já documenta a regra literal do produto — "Nunca usar
// 'Bloqueada' — o padrão do Maestro é sempre 'Aguardando conclusão de dependência'" —, mantida aqui
// por consistência com esse mesmo padrão já estabelecido.
export const LABEL_STATUS_ATIVIDADE: Record<StatusAtividade, string> = {
  NAO_INICIADA: 'Não iniciada',
  EM_ANDAMENTO: 'Em andamento',
  AGUARDANDO_DEPENDENCIA: 'Aguardando conclusão de dependência',
  AGUARDANDO_EVIDENCIA: 'Aguardando evidência',
  EM_REVISAO: 'Em revisão',
  CONCLUIDA: 'Concluída',
  ATRASADA: 'Atrasada',
  CANCELADA: 'Cancelada',
};

export const COR_STATUS_ATIVIDADE: Record<StatusAtividade, string> = {
  NAO_INICIADA: 'neutro',
  EM_ANDAMENTO: 'info',
  AGUARDANDO_DEPENDENCIA: 'warning',
  AGUARDANDO_EVIDENCIA: 'warning',
  EM_REVISAO: 'info',
  CONCLUIDA: 'success',
  ATRASADA: 'danger',
  CANCELADA: 'neutro',
};

export const LABEL_CRITICIDADE: Record<Criticidade, string> = {
  BAIXA: 'Baixa', MEDIA: 'Média', ALTA: 'Alta', CRITICA: 'Crítica',
};

export const COR_CRITICIDADE: Record<Criticidade, string> = {
  BAIXA: 'success', MEDIA: 'warning', ALTA: 'danger', CRITICA: 'dark',
};

export const LABEL_PRIORIDADE: Record<Prioridade, string> = {
  BAIXA: 'Baixa', NORMAL: 'Normal', ALTA: 'Alta', URGENTE: 'Urgente',
};

export const LABEL_TIPO_RECEITA: Record<TipoReceita, string> = {
  PRODUTO: 'Produto',
  SERVICO: 'Serviço',
  PRODUTO_E_SERVICO: 'Produto + Serviço',
};

export const LABEL_STATUS_CICLO: Record<StatusCiclo, string> = {
  NAO_INICIADO: 'Não iniciado',
  ABERTO: 'Aberto',
  EM_ANDAMENTO: 'Em andamento',
  EM_REVISAO: 'Em revisão',
  PRONTO_PARA_ENCERRAR: 'Pronto para encerrar',
  ENCERRADO: 'Encerrado',
  REABERTO: 'Reaberto',
};

export const LABEL_CLASSIFICACAO: Record<ClassificacaoDesempenho, string> = {
  EXCELENCIA: 'Excelência',
  ALTO_DESEMPENHO: 'Alto desempenho',
  BOM_DESEMPENHO: 'Bom desempenho',
  EM_EVOLUCAO: 'Em evolução',
  REQUER_ATENCAO: 'Requer atenção',
};

export const COR_CLASSIFICACAO: Record<ClassificacaoDesempenho, string> = {
  EXCELENCIA: 'success',
  ALTO_DESEMPENHO: 'success',
  BOM_DESEMPENHO: 'info',
  EM_EVOLUCAO: 'warning',
  REQUER_ATENCAO: 'danger',
};

export const LABEL_PRODUTO: Record<TipoProduto, string> = {
  MOTOR_CONCILIACAO: 'Motor de Conciliação',
  MAPA_RESULTADOS: 'Mapa de Resultados',
  REVISOR_CONTABIL: 'Revisor Contábil',
  WORKFLOW_SOX: 'Workflow SOX',
};

export const ICONE_PRODUTO: Record<TipoProduto, string> = {
  MOTOR_CONCILIACAO: 'bi bi-shuffle',
  MAPA_RESULTADOS: 'bi bi-graph-up-arrow',
  REVISOR_CONTABIL: 'bi bi-clipboard-check',
  WORKFLOW_SOX: 'bi bi-shield-lock',
};

export const LABEL_PERFIL_USUARIO: Record<PerfilUsuario, string> = {
  ADMINISTRADOR_PLATAFORMA: 'Administrador da Plataforma',
  TI: 'Tecnologia da Informação',
  DIRETORIA: 'Diretoria',
  CONTROLLER: 'Controller',
  GERENCIA_CONTABIL: 'Gerência Contábil',
  COORDENACAO_CONTABIL: 'Coordenação Contábil',
  ANALISTA_CONTABIL: 'Analista Contábil',
  PREPARADOR: 'Preparador',
  REVISOR: 'Revisor',
  APROVADOR: 'Aprovador',
  RESPONSAVEL_AREA: 'Responsável de Área',
  AUDITORIA: 'Auditoria',
  COMPLIANCE: 'Compliance',
  VISUALIZADOR: 'Visualizador',
  RESPONSAVEL_SOX: 'Responsável SOX',
};

// MAESTRO-RN-20 — versão simplificada dos níveis NA0–NA6 do épico (framework completo ainda não
// implementado, ver handoff), usada tanto na concessão de escopo quanto no simulador de acesso.
export const LABEL_NIVEL_ACESSO_MAESTRO: Record<NivelAcessoMaestro, string> = {
  CONSULTA: 'Consulta',
  OPERACAO: 'Operação',
  GESTAO: 'Gestão',
};

export const LABEL_SEVERIDADE_RISCO: Record<SeveridadeRisco, string> = {
  BAIXA: 'Baixa', MEDIA: 'Média', ALTA: 'Alta', CRITICA: 'Crítica',
};
export const COR_SEVERIDADE_RISCO: Record<SeveridadeRisco, string> = {
  BAIXA: 'neutro', MEDIA: 'info', ALTA: 'warning', CRITICA: 'danger',
};

export const LABEL_STATUS_RISCO: Record<StatusRisco, string> = {
  ABERTO: 'Aberto', EM_TRATAMENTO: 'Em tratamento', ESCALONADO: 'Escalonado', MITIGADO: 'Mitigado', ENCERRADO: 'Encerrado',
};
export const COR_STATUS_RISCO: Record<StatusRisco, string> = {
  ABERTO: 'neutro', EM_TRATAMENTO: 'info', ESCALONADO: 'danger', MITIGADO: 'success', ENCERRADO: 'dark',
};

export const LABEL_STATUS_DELEGACAO: Record<StatusDelegacao, string> = {
  ATIVA: 'Ativa', EXPIRADA: 'Expirada', REVOGADA: 'Revogada',
};
export const COR_STATUS_DELEGACAO: Record<StatusDelegacao, string> = {
  ATIVA: 'success', EXPIRADA: 'neutro', REVOGADA: 'danger',
};

export const LABEL_TIPO_FERIADO: Record<TipoFeriado, string> = {
  NACIONAL: 'Feriado nacional',
  ESTADUAL: 'Feriado estadual',
  MUNICIPAL: 'Feriado municipal',
  EMPRESA: 'Feriado específico da empresa',
};

export const LABEL_TIPO_EXCECAO: Record<TipoExcecao, string> = {
  DEPENDENCIA_BLOQUEANTE: 'Dependência bloqueante não cumprida',
  DADOS_DESATUALIZADOS: 'Dados desatualizados',
};

// MAESTRO-RN-43 (seção 16.9 do épico) — os 25 eventos de notificação implementados no backend
// (ver MaestroNotificationEventTypes.cs), na mesma ordem/agrupamento da classe de constantes.
export const EVENTOS_NOTIFICACAO: Array<{ valor: string; label: string }> = [
  { valor: 'ATIVIDADE_LIBERADA', label: 'Atividade liberada' },
  { valor: 'PROXIMIDADE_INICIO', label: 'Proximidade do início' },
  { valor: 'INICIO_NAO_REALIZADO_NO_PRAZO', label: 'Início não realizado no prazo' },
  { valor: 'INICIO_EFETIVO', label: 'Início efetivo' },
  { valor: 'PROXIMIDADE_VENCIMENTO', label: 'Proximidade do vencimento' },
  { valor: 'CONCLUSAO_ANTECIPADA', label: 'Conclusão antecipada' },
  { valor: 'CONCLUSAO_NO_PRAZO', label: 'Conclusão no prazo' },
  { valor: 'ATIVIDADE_VENCIDA', label: 'Atividade vencida' },
  { valor: 'CONCLUSAO_EM_ATRASO', label: 'Conclusão em atraso' },
  { valor: 'DEPENDENCIA_CONCLUIDA', label: 'Dependência concluída' },
  { valor: 'DEPENDENCIA_ATRASADA', label: 'Dependência atrasada' },
  { valor: 'ALTERACAO_PRAZO', label: 'Alteração de prazo' },
  { valor: 'ALTERACAO_RESPONSAVEL', label: 'Alteração de responsável' },
  { valor: 'EVIDENCIA_SOLICITADA', label: 'Evidência solicitada' },
  { valor: 'EVIDENCIA_REJEITADA', label: 'Evidência rejeitada' },
  { valor: 'DEVOLUCAO', label: 'Devolução' },
  { valor: 'PRONTIDAO_VALIDACAO', label: 'Prontidão para validação' },
  { valor: 'APROVACAO', label: 'Aprovação' },
  { valor: 'REJEICAO', label: 'Rejeição' },
  { valor: 'PDCA_CRIACAO', label: 'PDCA — criação' },
  { valor: 'PDCA_ATRIBUICAO', label: 'PDCA — atribuição' },
  { valor: 'PDCA_ATRASO', label: 'PDCA — atraso' },
  { valor: 'RISCO_ESCALONADO', label: 'Risco escalonado' },
  { valor: 'ENCERRAMENTO_CICLO', label: 'Encerramento de ciclo' },
  { valor: 'REABERTURA_CICLO', label: 'Reabertura de ciclo' },
];

export const LABEL_AREA: Record<TipoArea, string> = {
  TI: 'Tecnologia da Informação',
  RH: 'Recursos Humanos',
  FOLHA_PAGAMENTO: 'Folha de Pagamento',
  PATRIMONIO: 'Patrimônio',
  ESTOQUES: 'Estoques',
  TESOURARIA: 'Tesouraria',
  CONTAS_PAGAR: 'Contas a Pagar',
  CONTAS_RECEBER: 'Contas a Receber',
  COMERCIAL: 'Comercial',
  FATURAMENTO: 'Faturamento',
  FISCAL: 'Fiscal',
  TRIBUTOS_DIRETOS: 'Tributos Diretos',
  TRIBUTOS_INDIRETOS: 'Tributos Indiretos',
  OBRIGACOES_ACESSORIAS: 'Obrigações Acessórias',
  SOCIETARIO: 'Societário',
  CONTABILIDADE: 'Contabilidade',
  CONTROLADORIA: 'Controladoria',
  LANCAMENTOS_MANUAIS: 'Lançamentos Manuais',
  CONCILIACAO_CONTABIL: 'Conciliação Contábil',
  ANALISE_RESULTADOS: 'Análise de Resultados',
  REVISAO_CONTABIL: 'Revisão Contábil',
  WORKFLOW_SOX: 'Workflow SOX',
  OUTRAS: 'Outras Áreas',
};
